import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { WifiOff } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "advertiser" | "agency";
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading, networkError, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Загружаем сессию…</p>
      </div>
    );
  }

  if (networkError && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="glass-card w-full max-w-md p-6">
          <Alert variant="destructive">
            <WifiOff className="h-5 w-5" />
            <AlertTitle>Не удаётся связаться с сервером</AlertTitle>
            <AlertDescription className="mt-2 text-sm">
              Сервер авторизации не отвечает. Это может быть связано с ограничениями вашего интернет-провайдера.
              Попробуйте обновить страницу, переключиться на мобильный интернет или включить VPN.
            </AlertDescription>
          </Alert>
          <Button className="mt-4 w-full" onClick={() => window.location.reload()}>
            Повторить попытку
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/landing" replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
